/**
 * SignalChart — Real-time K-line signal chart
 *
 * 严格对齐 fortTrade-repo-master/client/src/components/SignalChart.tsx 的 UI：
 *   - 顶部：交易信号 + 1S/1M/5M 切换 + 反转点入口
 *   - 中部：实时折线（canvas 自绘）+ 末端涨跌跳动光圈
 *   - 反转点 L / S 标记 overlay，可点击查看信号详情
 *   - 末端价格标签
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { KlineSnapshot, KlineReversal } from "@/hooks/useMaxQuantKline";

interface SignalChartProps {
  snapshot: KlineSnapshot | null;
  symbol: string;
  interval: string;
  onIntervalChange?: (interval: string) => void;
  /** 由父组件根据 ws 状态、首包是否到达等计算 */
  loading?: boolean;
  /** 非空时显示错误区（与 loading 互斥由父组件保证） */
  loadError?: string | null;
  onLoadRetry?: () => void;
}

interface SignalDetail {
  direction: string;
  /** 原始方向，用于样式（勿用翻译后的 direction 判断） */
  directionTo: string;
  price: number;
  duration: string;
  /** 视口坐标，用于 fixed 弹层（避免被父级 overflow 裁切） */
  clientX: number;
  clientY: number;
}

const GREEN = "#00E676";
const RED = "#FF1744";
const LINE_COLOR = "#4A90FF";
const LONG_LINE = "rgba(34,197,94,0.9)";
const SHORT_LINE = "rgba(239,68,68,0.9)";

const PAD_TOP = 8;
const PAD_BOTTOM = 20;
const PAD_LEFT = 8;
// Reserve right-side breathing room so the latest point/ripple never clips.
const PAD_RIGHT = 12;
const CHART_HEIGHT = 80;
const LEFT_PINNED_POINT_INDEX = 14; // 左侧第5个点（0-based）

/** 将信号弹层放在点击点附近并限制在视口内（供 position: fixed 使用） */
function getSignalPopupPosition(clientX: number, clientY: number) {
  if (typeof window === "undefined") {
    return { left: clientX, top: clientY - 100 };
  }
  const pad = 8;
  const estW = 172;
  const estH = 128;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = clientX - estW / 2;
  let top = clientY - estH - 12;
  if (top < pad) {
    top = clientY + 16;
  }
  if (top + estH > vh - pad) {
    top = Math.max(pad, vh - estH - pad);
  }
  left = Math.max(pad, Math.min(left, vw - estW - pad));
  return { left, top };
}

function estimateTipY(points: { t: number; p: number }[], latest: number) {
  if (!points.length) return CHART_HEIGHT / 2;
  let min = Infinity;
  let max = -Infinity;
  for (const pt of points) {
    if (pt.p < min) min = pt.p;
    if (pt.p > max) max = pt.p;
  }
  const range = max - min;
  if (!Number.isFinite(range) || range <= 0) return CHART_HEIGHT / 2;
  const chartH = CHART_HEIGHT - PAD_TOP - PAD_BOTTOM;
  const pct = (latest - min) / range;
  const y = PAD_TOP + chartH * (1 - pct);
  return Math.max(PAD_TOP, Math.min(PAD_TOP + chartH, y));
}

export default function SignalChart({
  snapshot,
  symbol,
  interval,
  onIntervalChange,
  loading,
  loadError,
  onLoadRetry,
}: SignalChartProps) {
  const { t } = useTranslation();
  void symbol;

  const intervals = useMemo(
    () => [
      { label: t("aiTradingPage.chartIv1s"), value: "1s" as const },
      { label: t("aiTradingPage.chartIv1m"), value: "1m" as const },
      { label: t("aiTradingPage.chartIv5m"), value: "5m" as const },
    ],
    [t]
  );

  const directionLabel = useCallback(
    (to: string) => {
      if (to === "long") return t("aiTradingPage.chartDirLong");
      if (to === "short") return t("aiTradingPage.chartDirShort");
      return t("aiTradingPage.chartDirNeutral");
    },
    [t]
  );

  const formatDuration = useCallback(
    (fromTs: number, toTs: number) => {
      const diffMs = toTs - fromTs;
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) {
        const diffSec = Math.floor(diffMs / 1000);
        return t("aiTradingPage.durationSeconds", { count: diffSec });
      }
      if (diffMin < 60)
        return t("aiTradingPage.durationMinutes", { count: diffMin });
      const hours = Math.floor(diffMin / 60);
      const mins = diffMin % 60;
      return t("aiTradingPage.durationHoursMins", { hours, mins });
    },
    [t]
  );

  const formatPrice = useCallback(
    (price: number): string => {
      if (!Number.isFinite(price)) return t("aiTradingPage.chartPriceInvalid");
      return price.toFixed(2);
    },
    [t]
  );

  const [selectedSignal, setSelectedSignal] = useState<SignalDetail | null>(
    null
  );
  const [showReversals, setShowReversals] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const lineCanvasRef = useRef<HTMLCanvasElement>(null);
  const rippleCanvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const points = snapshot?.points ?? [];
  const reversals = snapshot?.reversals ?? [];
  const trendCurrent = snapshot?.trend?.current;
  const latestValue = points.length ? points[points.length - 1].p : 0;
  const tipY = useMemo(
    () => estimateTipY(points, latestValue),
    [points, latestValue]
  );
  const isLoading = !loadError && Boolean(loading);
  const latestRegime = useMemo<"long" | "short" | "neutral">(() => {
    const last = reversals.length ? reversals[reversals.length - 1] : null;
    if (last?.to === "long" || last?.to === "short") return last.to;
    if (trendCurrent === "long" || trendCurrent === "short")
      return trendCurrent;
    return "neutral";
  }, [reversals, trendCurrent]);

  /* ── 折线 canvas ── */
  useEffect(() => {
    const canvas = lineCanvasRef.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = parent.offsetWidth;
    const H = CHART_HEIGHT;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    if (!points.length) return;

    let min = Infinity;
    let max = -Infinity;
    for (const pt of points) {
      if (pt.p < min) min = pt.p;
      if (pt.p > max) max = pt.p;
    }
    const range = max - min || 1;
    const chartW = W - PAD_LEFT - PAD_RIGHT;
    const chartH = H - PAD_TOP - PAD_BOTTOM;
    const toX = (i: number) =>
      PAD_LEFT + (i / Math.max(1, points.length - 1)) * chartW;
    const toY = (p: number) => PAD_TOP + (1 - (p - min) / range) * chartH;

    ctx.strokeStyle = "rgba(255,255,255,0.04)";
    ctx.lineWidth = 0.5;
    for (let i = 0; i < 4; i++) {
      const y = PAD_TOP + (i / 3) * chartH;
      ctx.beginPath();
      ctx.moveTo(PAD_LEFT, y);
      ctx.lineTo(W - PAD_RIGHT, y);
      ctx.stroke();
    }

    const grad = ctx.createLinearGradient(0, PAD_TOP, 0, H - PAD_BOTTOM);
    grad.addColorStop(
      0,
      latestRegime === "long"
        ? "rgba(34,197,94,0.15)"
        : latestRegime === "short"
          ? "rgba(239,68,68,0.15)"
          : "rgba(74,144,255,0.18)"
    );
    grad.addColorStop(1, "rgba(74,144,255,0)");
    ctx.beginPath();
    ctx.moveTo(toX(0), H - PAD_BOTTOM);
    for (let i = 0; i < points.length; i++)
      ctx.lineTo(toX(i), toY(points[i].p));
    ctx.lineTo(toX(points.length - 1), H - PAD_BOTTOM);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();

    const visibleReversals = reversals
      .filter(r => r.t >= points[0].t && r.t <= points[points.length - 1].t)
      .slice()
      .sort((a, b) => a.t - b.t);
    const preVisible = reversals
      .filter(r => r.t < points[0].t)
      .slice()
      .sort((a, b) => a.t - b.t);
    let seg: "long" | "short" | "neutral" =
      preVisible.length > 0 &&
      (preVisible[preVisible.length - 1].to === "long" ||
        preVisible[preVisible.length - 1].to === "short")
        ? (preVisible[preVisible.length - 1].to as "long" | "short")
        : trendCurrent === "long" || trendCurrent === "short"
          ? trendCurrent
          : "neutral";
    let revIdx = 0;
    for (let i = 1; i < points.length; i++) {
      while (
        revIdx < visibleReversals.length &&
        visibleReversals[revIdx].t <= points[i].t
      ) {
        const nextTo = visibleReversals[revIdx].to;
        if (nextTo === "long" || nextTo === "short") seg = nextTo;
        revIdx += 1;
      }
      ctx.beginPath();
      ctx.moveTo(toX(i - 1), toY(points[i - 1].p));
      ctx.lineTo(toX(i), toY(points[i].p));
      ctx.strokeStyle =
        seg === "long" ? LONG_LINE : seg === "short" ? SHORT_LINE : LINE_COLOR;
      ctx.lineWidth = 1.5;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.stroke();
    }

    const lastX = toX(points.length - 1);
    const lastY = toY(points[points.length - 1].p);
    ctx.beginPath();
    ctx.arc(lastX, lastY, 3.2, 0, Math.PI * 2);
    ctx.fillStyle =
      latestRegime === "long"
        ? LONG_LINE
        : latestRegime === "short"
          ? SHORT_LINE
          : LINE_COLOR;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(lastX, lastY, 5.6, 0, Math.PI * 2);
    ctx.strokeStyle =
      latestRegime === "long"
        ? "rgba(34,197,94,0.35)"
        : latestRegime === "short"
          ? "rgba(239,68,68,0.35)"
          : "rgba(74,144,255,0.35)";
    ctx.lineWidth = 1;
    ctx.stroke();

    // Bottom HH:MM time axis（与 AiTrade liveline.formatTime 一致）
    const labelCount: number = 4;
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, Monaco, monospace";
    ctx.textBaseline = "middle";
    for (let i = 0; i < labelCount; i++) {
      const ratio = labelCount <= 1 ? 0 : i / (labelCount - 1);
      const idx = Math.round(ratio * (points.length - 1));
      const ts = points[idx]?.t;
      if (!ts) continue;
      const d = new Date(ts);
      const hh = d.getHours().toString().padStart(2, "0");
      const mm = d.getMinutes().toString().padStart(2, "0");
      const text = `${hh}:${mm}`;
      const x = toX(idx);
      if (i === 0) ctx.textAlign = "left";
      else if (i === labelCount - 1) ctx.textAlign = "right";
      else ctx.textAlign = "center";
      ctx.fillText(text, x, H - PAD_BOTTOM / 2 + 2);
    }
  }, [points, reversals, trendCurrent, latestRegime]);

  /* ── 涨跌跳动光圈 ── */
  const prevPriceRef = useRef(0);
  const lastDirRef = useRef<"up" | "down" | null>(null);
  const ripplesRef = useRef<
    {
      x: number;
      y: number;
      radius: number;
      maxRadius: number;
      color: string;
      alpha: number;
      birth: number;
    }[]
  >([]);
  const lastBirthRef = useRef(0);
  const animRef = useRef(0);

  useEffect(() => {
    const canvas = rippleCanvasRef.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let w = 0;
    let h = 0;
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = parent.offsetWidth;
      h = parent.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      const now = Date.now();
      ctx.clearRect(0, 0, w, h);
      const dotX = w - PAD_RIGHT - 4;
      const dotY = tipY;
      if (latestValue > 0 && prevPriceRef.current > 0) {
        const isUp = latestValue > prevPriceRef.current;
        const isDown = latestValue < prevPriceRef.current;
        const dirNow: "up" | "down" = isUp ? "up" : "down";
        const dirChanged = dirNow !== lastDirRef.current;
        const sinceLast = now - lastBirthRef.current;
        if ((isUp || isDown) && (dirChanged || sinceLast > 2000)) {
          lastDirRef.current = dirNow;
          lastBirthRef.current = now;
          ripplesRef.current.push({
            x: dotX,
            y: dotY,
            radius: 4,
            maxRadius: 55 + Math.random() * 25,
            color: isUp ? GREEN : RED,
            alpha: 0.7,
            birth: now,
          });
          if (ripplesRef.current.length > 8)
            ripplesRef.current = ripplesRef.current.slice(-8);
        }
      }
      if (latestValue > 0) prevPriceRef.current = latestValue;

      const alive: typeof ripplesRef.current = [];
      for (const r of ripplesRef.current) {
        const age = now - r.birth;
        const life = 2000;
        const progress = age / life;
        if (progress >= 1) continue;
        r.radius = 4 + progress * r.maxRadius;
        r.alpha = 0.6 * (1 - progress) * (1 - progress);
        for (let ring = 0; ring < 3; ring++) {
          const rr = r.radius * (1 - ring * 0.2);
          if (rr <= 0) continue;
          ctx.beginPath();
          ctx.arc(r.x, r.y, rr, 0, Math.PI * 2);
          ctx.strokeStyle = r.color;
          ctx.lineWidth = 2 - ring * 0.5;
          ctx.globalAlpha = r.alpha * (1 - ring * 0.3);
          ctx.stroke();
        }
        const rg = ctx.createRadialGradient(
          r.x,
          r.y,
          0,
          r.x,
          r.y,
          r.radius * 0.35
        );
        rg.addColorStop(0, r.color);
        rg.addColorStop(1, "transparent");
        ctx.fillStyle = rg;
        ctx.globalAlpha = r.alpha * 0.15;
        ctx.beginPath();
        ctx.arc(r.x, r.y, r.radius * 0.35, 0, Math.PI * 2);
        ctx.fill();
        alive.push(r);
      }
      ripplesRef.current = alive;
      ctx.globalAlpha = 1;
      animRef.current = requestAnimationFrame(draw);
    };
    animRef.current = requestAnimationFrame(draw);
    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animRef.current);
    };
  }, [latestValue, tipY]);

  /* ── 反转点 markers ── */
  const reversalMarkers = useMemo(() => {
    if (!reversals.length || !points.length) return [];
    const minT = points[0].t;
    const maxT = points[points.length - 1].t;
    const timeRange = maxT - minT;
    if (timeRange <= 0) return [];

    let minP = Infinity;
    let maxP = -Infinity;
    for (const p of points) {
      if (p.p < minP) minP = p.p;
      if (p.p > maxP) maxP = p.p;
    }
    const priceRange = maxP - minP || 1;
    const markerHalfH = 6;
    const minMarkerY = PAD_TOP + markerHalfH;
    const maxMarkerY = CHART_HEIGHT - PAD_BOTTOM - markerHalfH - 1;

    return reversals
      .filter(r => r.t >= minT && r.t <= maxT)
      .map((r, idx, arr) => {
        const xPct = ((r.t - minT) / timeRange) * 100;
        const rawY =
          PAD_TOP +
          (1 - (r.p - minP) / priceRange) *
            (CHART_HEIGHT - PAD_TOP - PAD_BOTTOM);
        const safeY = Math.max(minMarkerY, Math.min(maxMarkerY, rawY));
        const yPct = (safeY / CHART_HEIGHT) * 100;
        const next = arr[idx + 1];
        const endTs = next ? next.t : Date.now();
        const duration = formatDuration(r.t, endTs);
        return {
          ...r,
          xPct,
          yPct,
          label: r.to === "long" ? "L" : "S",
          color: r.to === "long" ? "#00d4aa" : "#ff4d6a",
          bgColor:
            r.to === "long" ? "rgba(0,212,170,0.15)" : "rgba(255,77,106,0.15)",
          direction: directionLabel(r.to),
          duration,
          idx,
        };
      });
  }, [reversals, points, directionLabel, formatDuration]);

  const latestSignalVisible = useMemo(() => {
    const last = reversals.length ? reversals[reversals.length - 1] : null;
    if (!last || (last.to !== "long" && last.to !== "short")) return false;
    if (!points.length) return false;
    return last.t >= points[0].t && last.t <= points[points.length - 1].t;
  }, [reversals, points]);

  const pinnedLeftPoint = useMemo(() => {
    if (!points.length) return null;
    const idx = Math.min(LEFT_PINNED_POINT_INDEX, points.length - 1);
    let minP = Infinity;
    let maxP = -Infinity;
    for (const p of points) {
      if (p.p < minP) minP = p.p;
      if (p.p > maxP) maxP = p.p;
    }
    const priceRange = maxP - minP || 1;
    const markerHalfH = 6;
    const minMarkerY = PAD_TOP + markerHalfH;
    const maxMarkerY = CHART_HEIGHT - PAD_BOTTOM - markerHalfH - 1;
    const xPct = (idx / Math.max(1, points.length - 1)) * 100;
    const rawY =
      PAD_TOP +
      (1 - (points[idx].p - minP) / priceRange) *
        (CHART_HEIGHT - PAD_TOP - PAD_BOTTOM);
    const safeY = Math.max(minMarkerY, Math.min(maxMarkerY, rawY));
    const yPct = (safeY / CHART_HEIGHT) * 100;
    return { xPct, yPct };
  }, [points]);

  const handleMarkerClick = useCallback(
    (marker: (typeof reversalMarkers)[number], e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedSignal({
        direction: marker.direction,
        directionTo: marker.to,
        price: marker.p,
        duration: marker.duration,
        clientX: e.clientX,
        clientY: e.clientY,
      });
    },
    []
  );

  useEffect(() => {
    if (!selectedSignal) return;
    const close = () => setSelectedSignal(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [selectedSignal]);

  return (
    <>
      <div className="relative w-full" ref={containerRef}>
        {/* Header: 交易信号 + interval switcher + 反转点 */}
        <div className="flex items-center justify-between mb-1 px-1">
          <span className="text-sm text-gray-400 tracking-wide shrink-0">
            {t("aiTradingPage.chartSignalTitle")}
          </span>
          <div className="flex items-center gap-0.5 bg-gray-800/60 rounded-md p-0.5 shrink-0">
            {intervals.map(iv => (
              <button
                key={iv.value}
                onClick={() => onIntervalChange?.(iv.value)}
                className={`px-2.5 py-0.5 text-xs font-medium rounded transition-all ${
                  interval === iv.value
                    ? "bg-gray-600/80 text-white"
                    : "text-gray-500 hover:text-gray-300"
                }`}
              >
                {iv.label}
              </button>
            ))}
          </div>
          {/* <div className="flex items-center gap-2 shrink-0">
          <button
            className={`text-xs transition-colors ${
              showReversals
                ? "text-cyan-400"
                : "text-gray-500 hover:text-cyan-400"
            }`}
            onClick={() => {
              setShowReversals((v) => !v);
              setSelectedSignal(null);
            }}
            aria-pressed={showReversals}
            title={showReversals ? "点击隐藏反转点" : "点击显示反转点"}
          >
            反转点
          </button>
        </div> */}
        </div>

        {/* Chart area */}
        <div className="relative w-full" style={{ height: CHART_HEIGHT }}>
          {loadError && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-[#0a0a1e]/75 backdrop-blur-[2px] rounded-lg px-2 text-center">
              <span className="text-[11px] text-gray-400 leading-snug">
                {loadError}
              </span>
              {onLoadRetry && (
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    onLoadRetry();
                  }}
                  className="text-[11px] font-medium text-cyan-400 hover:text-cyan-300"
                >
                  {t("aiTradingPage.chartRetry")}
                </button>
              )}
            </div>
          )}
          {isLoading && !loadError && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-[#0a0a1e]/60 backdrop-blur-[2px] rounded-lg">
              <div className="flex items-center gap-1.5">
                {[0, 1, 2].map(i => (
                  <span
                    key={i}
                    className="w-1.5 h-1.5 rounded-full bg-cyan-400/60"
                    style={{
                      animation: "pulse 1.2s ease-in-out infinite",
                      animationDelay: `${i * 200}ms`,
                    }}
                  />
                ))}
                <span className="text-xs text-gray-500 ml-1">
                  {t("aiTradingPage.chartLoading")}
                </span>
              </div>
            </div>
          )}

          <canvas
            ref={lineCanvasRef}
            className="absolute inset-0"
            style={{ zIndex: 1 }}
          />
          <canvas
            ref={rippleCanvasRef}
            className="absolute inset-0 pointer-events-none"
            style={{ zIndex: 5 }}
          />

          {/* Signal markers overlay — 仅在 showReversals 时绘制 */}
          {showReversals ? (
            <div
              ref={overlayRef}
              className="absolute inset-0 pointer-events-none"
              style={{
                padding: `${PAD_TOP}px ${PAD_RIGHT}px ${PAD_BOTTOM}px ${PAD_LEFT}px`,
                zIndex: 10,
              }}
            >
              {reversalMarkers.map(
                (
                  marker: KlineReversal & {
                    xPct: number;
                    yPct: number;
                    label: string;
                    color: string;
                    bgColor: string;
                    direction: string;
                    duration: string;
                    idx: number;
                  }
                ) => (
                  <div
                    key={`${marker.t}-${marker.idx}`}
                    className="absolute pointer-events-auto cursor-pointer transform -translate-x-1/2 -translate-y-1/2 z-10"
                    style={{
                      left: `${marker.xPct}%`,
                      top: `${marker.yPct}%`,
                    }}
                    onClick={e => handleMarkerClick(marker, e)}
                  >
                    <div
                      className="flex items-center justify-center rounded-sm text-[10px] font-bold shadow-lg transition-transform hover:scale-110"
                      style={{
                        width: 14,
                        height: 12,
                        backgroundColor:
                          marker.label === "L"
                            ? "rgba(34,197,94,0.85)"
                            : "rgba(239,68,68,0.85)",
                        color: "#fff",
                      }}
                    >
                      {marker.label}
                    </div>
                  </div>
                )
              )}

              {/* 当最新反转点已经滚出可视区时，常驻在左侧第5个点并压在K线上 */}
              {latestRegime !== "neutral" &&
              !latestSignalVisible &&
              pinnedLeftPoint ? (
                <div
                  className="absolute pointer-events-none transform -translate-x-1/2 -translate-y-1/2 z-11"
                  style={{
                    left: `${pinnedLeftPoint.xPct}%`,
                    top: `${pinnedLeftPoint.yPct}%`,
                  }}
                >
                  <div
                    className="flex items-center justify-center rounded-sm text-[10px] font-bold shadow-lg"
                    style={{
                      width: 14,
                      height: 12,
                      backgroundColor:
                        latestRegime === "long"
                          ? "rgba(34,197,94,0.85)"
                          : "rgba(239,68,68,0.85)",
                      color: "#fff",
                    }}
                  >
                    {latestRegime === "long" ? "L" : "S"}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Latest price label */}
        {points.length ? (
          <div
            className="absolute pointer-events-none z-6"
            style={{
              right: PAD_RIGHT + 2,
              top: Math.max(
                Math.min(tipY - 10, CHART_HEIGHT - 4),
                PAD_TOP + 20
              ),
            }}
          >
            <span
              className="text-[11px] font-mono font-bold drop-shadow-lg"
              style={{
                color:
                  lastDirRef.current === "up"
                    ? GREEN
                    : lastDirRef.current === "down"
                      ? RED
                      : LINE_COLOR,
              }}
            >
              {formatPrice(latestValue)}
            </span>
          </div>
        ) : null}
      </div>

      {showReversals && selectedSignal
        ? createPortal(
            <div
              className="pointer-events-auto"
              style={{
                position: "fixed",
                zIndex: 10050,
                ...getSignalPopupPosition(
                  selectedSignal.clientX,
                  selectedSignal.clientY
                ),
              }}
              onClick={e => e.stopPropagation()}
              role="dialog"
              aria-label={t("aiTradingPage.chartDetailAria")}
            >
              <div className="bg-gray-900/95 border border-gray-700 rounded-lg p-3 shadow-xl backdrop-blur-sm min-w-[140px] max-w-[min(92vw,220px)]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] text-gray-400">
                    {t("aiTradingPage.chartDetailTitle")}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedSignal(null)}
                    className="text-gray-500 hover:text-white text-xs"
                  >
                    ✕
                  </button>
                </div>
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">
                      {t("aiTradingPage.chartStrategyDir")}
                    </span>
                    <span
                      className="text-xs font-bold"
                      style={{
                        color:
                          selectedSignal.directionTo === "long"
                            ? "#00d4aa"
                            : selectedSignal.directionTo === "short"
                              ? "#ff4d6a"
                              : "#94a3b8",
                      }}
                    >
                      {selectedSignal.direction}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">
                      {t("aiTradingPage.chartPrice")}
                    </span>
                    <span className="text-xs text-white font-mono">
                      {formatPrice(selectedSignal.price)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">
                      {t("aiTradingPage.chartTrendDuration")}
                    </span>
                    <span className="text-xs text-gray-300">
                      {selectedSignal.duration}
                    </span>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
